// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ScrollIndicator } from '@/components/scroll-indicator';
import { Search as SearchIcon } from '@/constants/hardfin-standard-icons';
import { useScrollIndicator } from '@/hooks/use-scroll-indicator';
import { Item } from '@/items/api/types';
import type { ItemType } from '@/items/constants/item-types';
import { useItems } from '@/items/hooks/use-items';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash-es';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';

interface Props {
  type: ItemType;
  onItemSelect: (itemKey: string) => void;
  excludeItemId?: string;
}

export function ItemSelectionList(props: Props) {
  const { items, isLoading } = useItems();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const scrollIndicator = useScrollIndicator();

  const handleClickScroll = () => {
    if (scrollIndicator.element === null) {
      return;
    }

    scrollIndicator.element.scrollTo({
      top: scrollIndicator.element.scrollHeight,
      behavior: 'smooth',
    });

    scrollIndicator.hide();
  };

  const debouncedSetSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        setDebouncedSearch(searchValue);
      }, 300),
    [setDebouncedSearch]
  );

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchValue(value);
      debouncedSetSearch(value);
    },
    [setSearchValue, debouncedSetSearch]
  );

  const filteredItems = useMemo(() => {
    // Filter to only items of the same type and exclude the current item
    let filtered = items.filter(
      (item) => item.id !== props.excludeItemId && item.type === props.type
    );

    if (debouncedSearch.trim() !== '') {
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          item.sku.toLowerCase().includes(debouncedSearch.toLowerCase())
      );
    }

    return filtered;
  }, [items, props.excludeItemId, props.type, debouncedSearch]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <TextField
        placeholder="Search items"
        value={searchValue}
        onChange={handleSearchChange}
        variant="outlined"
        size="small"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <Paper
        variant="outlined"
        sx={{ maxHeight: 400, overflow: 'auto', position: 'relative' }}
        ref={scrollIndicator.ref}
      >
        <ScrollIndicator
          label="More"
          isVisible={scrollIndicator.isVisible}
          onClick={handleClickScroll}
        />
        <List dense>
          {filteredItems.map((item, index) => (
            <Box key={item.id}>
              <ItemListItem
                item={item}
                onSelect={() => props.onItemSelect(item.key)}
              />
              {index < filteredItems.length - 1 && (
                <Divider sx={{ width: '90%', mx: 'auto' }} />
              )}
            </Box>
          ))}
          {filteredItems.length === 0 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <Typography variant="body2" color="text.secondary">
                No items found for {searchValue}
              </Typography>
            </Box>
          )}
        </List>
      </Paper>
    </Stack>
  );
}

interface ItemListItemProps {
  item: Item;
  onSelect: () => void;
}

function ItemListItem(props: ItemListItemProps) {
  return (
    <ListItem disablePadding>
      <ListItemButton onClick={props.onSelect}>
        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1">
                {props.item.sku}
                <Typography
                  variant="body1"
                  color="text.secondary"
                  component="span"
                  sx={{ ml: 1 }}
                >
                  ({props.item.name})
                </Typography>
              </Typography>
              {props.item.isArchived && (
                <Chip
                  size="small"
                  label="Archived"
                  variant="outlined"
                  color="default"
                />
              )}
              <Chip
                size="small"
                label={props.item.type === 'SERVICE' ? 'Service' : 'Device'}
                color="primary"
              />
              {props.item.integrations
                .filter((link) => link.link !== null && link.id !== 'NONE')
                .map((link) => (
                  <Chip
                    key={link.id}
                    size="small"
                    label={link.label}
                    color="secondary"
                  />
                ))}
            </Box>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              {pluralize(props.item.assetCount ?? 0, {
                plural: 'assets',
                singular: 'asset',
              })}
            </Typography>
          }
        />
      </ListItemButton>
    </ListItem>
  );
}
